import React, { useCallback, useMemo, useRef, useState } from 'react';

import { hasChildByName } from 'helpers';
import { MenuItem } from 'primereact/menuitem';
import { Toast } from 'primereact/toast';
import { DetailedInformationCardProps, TreeNode } from 'types/interfaces';

import { DepartmentsService } from 'components/services';

import { DetailedInformationTemplate } from '../../constants';

const setData = (nodes: TreeNode[], hasParent: boolean) => {
  nodes.forEach((x) => {
    if (x.isPerson) x.data = 'Сотрудник';
    else if (x.children && x.children.length > 0) {
      x.data = x.children.some((y: TreeNode) => y.isPerson)
        ? 'Участок'
        : 'Подразделение';
      setData(x.children, true);
    } else x.data = 'Участок';
  });
};

const findParent = (
  nodes: TreeNode[],
  key: string
): { node: TreeNode | undefined; found: boolean } => {
  let found: boolean = false;
  let node: TreeNode | undefined = undefined;
  nodes.forEach((x) => {
    if (!found) {
      if (x.key === key) {
        found = true;
      } else if (x.children) {
        if (x.children.some((y) => y.key === key)) {
          found = true;
          node = x;
        } else {
          const result = findParent(x.children, key);
          found = result.found;
          node = result.node;
        }
      }
    }
  });
  return { node, found };
};

interface UseFunctionalStructureReturn {
  // Состояния
  treeNodes: TreeNode[];
  selectedKey: string;
  expandedKeys: { [key: string]: boolean };
  selectedItem: TreeNode | null;
  toast: React.RefObject<Toast>;
  items: MenuItem[];
  employeeCardDirty: boolean;
  detailedCardDirty: boolean;
  addEmployeeCardDirty: boolean;
  hasUnsavedChanges: boolean;
  showUnsavedDialog: boolean;
  createOrganization: boolean;
  createDepartment: boolean;
  createEmployee: boolean;
  dialogVisible: boolean;
  pendingActionRef: React.MutableRefObject<(() => void) | null>;
  detailedInformationCardProps: DetailedInformationCardProps;
  // Методы
  getDepartments: (id?: number) => Promise<void>;
  setSelectedKey: React.Dispatch<React.SetStateAction<string>>;
  findParent: (
    nodes: TreeNode[],
    key: string
  ) => { node: TreeNode | undefined; found: boolean };
  setExpandedKeys: React.Dispatch<
    React.SetStateAction<{ [key: string]: boolean }>
  >;
  setSelectedItem: React.Dispatch<React.SetStateAction<TreeNode | null>>;
  runGuarded: (action: () => void) => void;
  setEmployeeCardDirty: React.Dispatch<React.SetStateAction<boolean>>;
  setDetailedCardDirty: React.Dispatch<React.SetStateAction<boolean>>;
  setAddEmployeeCardDirty: React.Dispatch<React.SetStateAction<boolean>>;
  setShowUnsavedDialog: React.Dispatch<React.SetStateAction<boolean>>;
  setCreateOrganization: React.Dispatch<React.SetStateAction<boolean>>;
  setCreateDepartment: React.Dispatch<React.SetStateAction<boolean>>;
  setCreateEmployee: React.Dispatch<React.SetStateAction<boolean>>;
  setDialogVisible: React.Dispatch<React.SetStateAction<boolean>>;
  handleUnsavedContinue: () => void;
  handleUnsavedCancel: () => void;
}

export function useFunctionalStructure(): UseFunctionalStructureReturn {
  const [treeNodes, setTreeNodes] = useState<TreeNode[]>([]);
  const [selectedKey, setSelectedKey] = useState<string>('');
  const [expandedKeys, setExpandedKeys] = useState<{ [key: string]: boolean }>(
    {}
  );
  const [selectedItem, setSelectedItem] = useState<TreeNode | null>(null);
  const [employeeCardDirty, setEmployeeCardDirty] = useState<boolean>(false);
  const [detailedCardDirty, setDetailedCardDirty] = useState<boolean>(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState<boolean>(false);
  const [dialogVisible, setDialogVisible] = useState<boolean>(false);
  const [addEmployeeCardDirty, setAddEmployeeCardDirty] =
    useState<boolean>(false);
  const [createOrganization, setCreateOrganization] = useState<boolean>(false);
  const [createDepartment, setCreateDepartment] = useState<boolean>(false);
  const [createEmployee, setCreateEmployee] = useState<boolean>(false);
  const hasUnsavedChanges =
    employeeCardDirty || detailedCardDirty || addEmployeeCardDirty;
  const toast = useRef<Toast>(null);
  const pendingActionRef = useRef<null | (() => void)>(null);

  const runGuarded = useCallback(
    (action: () => void) => {
      if (hasUnsavedChanges) {
        pendingActionRef.current = action;
        setShowUnsavedDialog(true);
        return;
      }
      action();
    },
    [hasUnsavedChanges]
  );

  const getDepartments = useCallback(async (id?: number) => {
    const result = await DepartmentsService.readForFunctionalStructure();
    if (result.data) {
      setData(result.data.value, false);
      setTreeNodes([...result.data.value]);
      if (id) setSelectedKey(id.toString());
    }
  }, []);

  const items: MenuItem[] = [
    {
      items: [
        {
          label: 'Создать участок',
          icon: 'pi pi-map-marker',
          command: () => {
            runGuarded(() => {
              if (toast.current && !selectedItem) {
                toast.current.show({
                  severity: 'warn',
                  summary: 'Ошибка',
                  detail: 'Выберите элемент',
                  life: 3000
                });
              }
              if (
                toast.current &&
                selectedItem &&
                selectedItem.data === 'Сотрудник'
              ) {
                toast.current.show({
                  severity: 'warn',
                  summary: 'Ошибка',
                  detail: 'Невозможно добавить участок к элементу Сотрудник',
                  life: 3000
                });
              }
              if (
                toast.current &&
                selectedItem &&
                hasChildByName(selectedItem, 'Сотрудник')
              ) {
                toast.current.show({
                  severity: 'warn',
                  summary: 'Ошибка',
                  detail:
                    'Невозможно добавить участок для элементов, имеющих дочерние элементы типа Сотрудник',
                  life: 3000
                });
              }
              if (
                toast.current &&
                selectedItem &&
                selectedItem.data !== 'Сотрудник' &&
                !hasChildByName(selectedItem, 'Сотрудник')
              ) {
                setCreateOrganization(false);
                setCreateEmployee(false);
                setCreateDepartment(true);
              }
            });
          }
        },
        {
          label: 'Создать нового сотрудника',
          icon: 'pi pi-isUser-plus',
          command: () => {
            runGuarded(() => {
              if (toast.current && !selectedItem) {
                toast.current.show({
                  severity: 'warn',
                  summary: 'Ошибка',
                  detail: 'Выберете участок',
                  life: 3000
                });
              }
              if (
                toast.current &&
                selectedItem &&
                selectedItem.data === 'Сотрудник'
              ) {
                toast.current.show({
                  severity: 'warn',
                  summary: 'Ошибка',
                  detail: 'Невозможно добавить сотрудника к элементу Сотрудник',
                  life: 3000
                });
              }
              if (
                toast.current &&
                selectedItem &&
                !hasChildByName(selectedItem, 'Сотрудник') &&
                (hasChildByName(selectedItem, 'Участок') ||
                  hasChildByName(selectedItem, 'Подразделение'))
              ) {
                toast.current.show({
                  severity: 'warn',
                  summary: 'Ошибка',
                  detail:
                    'Для участков и подразделений без сотрудников, можно создать только Участок',
                  life: 3000
                });
              }
              if (
                toast.current &&
                selectedItem &&
                selectedItem.data !== 'Сотрудник' &&
                (hasChildByName(selectedItem, 'Сотрудник') ||
                  !selectedItem.hasOwnProperty('children') ||
                  (!hasChildByName(selectedItem, 'Участок') &&
                    !hasChildByName(selectedItem, 'Подразделение')))
              ) {
                setCreateEmployee(true);
                setCreateOrganization(false);
                setCreateDepartment(false);
              }
            });
          }
        },
        {
          label: 'Добавить существующих сотрудников',
          icon: 'pi pi-plus',
          command: () => {
            runGuarded(() => {
              if (
                selectedItem &&
                selectedItem.data === 'Сотрудник' &&
                toast.current
              )
                toast.current.show({
                  severity: 'warn',
                  summary: 'Ошибка',
                  detail:
                    'Невозможно добавить сотрудников для выбранного объекта Сотрудник',
                  life: 3000
                });
              if (!selectedItem && toast.current)
                toast.current.show({
                  severity: 'warn',
                  summary: 'Ошибка',
                  detail: 'Выберите елемент для добавления сотрудников',
                  life: 3000
                });
              if (
                selectedItem &&
                selectedItem.data === 'Подразделение' &&
                toast.current
              )
                toast.current.show({
                  severity: 'warn',
                  summary: 'Ошибка',
                  detail:
                    'Невозможно добавить сотрудников, пожалуйста выберите Участок для данной операции',
                  life: 3000
                });
              if (
                hasChildByName(selectedItem, 'Сотрудник') ||
                (selectedItem?.children &&
                  selectedItem.data !== 'Сотрудник' &&
                  selectedItem?.children?.length < 1)
              ) {
                setCreateDepartment(false);
                setDialogVisible(true);
              }
            });
          }
        },
        {
          label: 'Создать ветку участков',
          icon: 'pi pi-share-alt',
          command: () => {
            runGuarded(() => {
              setCreateOrganization(true);
              setCreateEmployee(false);
              setCreateDepartment(false);
            });
          }
        }
      ]
    }
  ];

  const detailedInformationCardProps =
    useMemo<DetailedInformationCardProps>(() => {
      let data: DetailedInformationCardProps = {
        ...DetailedInformationTemplate,
        key: '',
        id: '',
        parentKey: '',
        data: '',
        updateDetails: async (newDetails: any) => {
          if (toast.current)
            toast.current.show({
              severity: 'success',
              summary: 'Успешно сохранено',
              detail: 'Департамент обновлен',
              life: 3000
            });
          await getDepartments();
        },
        onError: (errorMessage: string) => {
          if (toast.current) {
            toast.current.show({
              severity: 'error',
              summary: 'Ошибка',
              detail: errorMessage,
              life: 3000
            });
          }
        }
      };

      if (createDepartment) {
        data = {
          ...data,
          key: `${Date.now()}`,
          id: selectedItem?.id ?? '',
          parentKey: selectedItem?.key ?? '',
          organization: selectedItem?.label,
          label: '',
          data: 'Участок',
          isFias: true,
          onAdd: async (newDetails: any) => {
            try {
              await getDepartments(newDetails?.id);
              if (toast.current)
                toast.current.show({
                  severity: 'success',
                  summary: 'Успешно сохранено',
                  detail: 'Департамент добавлен',
                  life: 3000
                });
            } catch (error: any) {
              // Обработка ошибок для createDepartment
              if (
                error.response?.status === 400 &&
                error.response?.data?.error === 'SameName'
              ) {
                if (toast.current) {
                  toast.current.show({
                    severity: 'error',
                    summary: 'Ошибка',
                    detail: 'Подразделение с таким именем уже существует',
                    life: 3000
                  });
                }
              }
            }
          }
        };
      } else if (createOrganization) {
        data = {
          ...data,
          key: `${Date.now()}`,
          id: selectedItem?.id ?? '',
          parentKey: '',
          label: '',
          data: 'Подразделение',
          onAdd: async (newDetails: any) => {
            try {
              await getDepartments();
              if (toast.current)
                toast.current.show({
                  severity: 'success',
                  summary: 'Успешно сохранено',
                  detail: 'Подразделение добавлено',
                  life: 3000
                });
            } catch (error: any) {
              if (
                error.response?.status === 400 &&
                error.response?.data?.error === 'SameName'
              ) {
                if (toast.current) {
                  toast.current.show({
                    severity: 'error',
                    summary: 'Ошибка',
                    detail: 'Подразделение с таким именем уже существует',
                    life: 3000
                  });
                }
              }
            }
          }
        };
      } else if (selectedItem !== null) {
        const parent = findParent(treeNodes, selectedItem.key).node;
        const parentKey = parent?.key ?? '';
        const organization = parent?.label ?? '';

        data = {
          ...data,
          ...selectedItem,
          id: selectedItem.key,
          label: selectedItem.label ?? '',
          organization: organization,
          parentKey: parentKey
        };
      }
      return data;
    }, [
      createDepartment,
      createOrganization,
      selectedItem,
      getDepartments,
      treeNodes
    ]);

  const handleUnsavedCancel = useCallback(() => {
    setShowUnsavedDialog(false);
    pendingActionRef.current = null;
  }, []);

  const handleUnsavedContinue = useCallback(() => {
    setShowUnsavedDialog(false);
    // user explicitly chose to continue without saving -> drop dirty flags
    setEmployeeCardDirty(false);
    setDetailedCardDirty(false);
    setAddEmployeeCardDirty(false);
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    action?.();
  }, []);

  return {
    // Состояния
    treeNodes,
    selectedKey,
    expandedKeys,
    selectedItem,
    toast,
    items,
    employeeCardDirty,
    detailedCardDirty,
    addEmployeeCardDirty,
    hasUnsavedChanges,
    pendingActionRef,
    showUnsavedDialog,
    createOrganization,
    createDepartment,
    createEmployee,
    dialogVisible,
    detailedInformationCardProps,

    // Методы
    getDepartments,
    findParent,
    setSelectedKey,
    setExpandedKeys,
    setSelectedItem,
    runGuarded,
    setEmployeeCardDirty,
    setDetailedCardDirty,
    setAddEmployeeCardDirty,
    setShowUnsavedDialog,
    setCreateOrganization,
    setCreateDepartment,
    setCreateEmployee,
    handleUnsavedContinue,
    setDialogVisible,
    handleUnsavedCancel
  };
}
